(ns app.front.ui.actions
  "Primary actions bar"
  (:require [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.front.view.workspace.action-subs :as action-subs]
            [app.front.view.workspace.primary-actions :as primary-actions]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]))

(defn- params-valid?
  [params]
  (->> (vals params)
       (every? (fn [value]
                 (cond
                   (string? value) (not (str/blank? value))
                   :else           (some? value))))))

(defn- button-default-opts [signal-id]
  {:key              signal-id
   :kind             :primary
   :class-name       "process-action"
   :icon             (primary-actions/signal-icons signal-id i/arrow-right)
   :icon-description ""
   :icon-position    :right})

(defn- signal-button-impl [{:keys [confirm? tr button-opts params confirm-title target-id signal-id params-valid?]}
                           confirm-content]
  (let [signal               [signal-id params]
        signal-send-fn       #(>evt [::process/signal target-id signal])
        processing-signals   (<sub [::process/current-processing-signals])
        signal-not-permitted (not (<sub [::process/signal-permitted? target-id signal]))
        button-opts          (merge (button-default-opts signal-id)
                                    button-opts
                                    {:disabled (boolean (or signal-not-permitted
                                                            (:disabled button-opts)))})]
    (if (contains? processing-signals signal-id)
      (cc/inline-loading {:description (primary-actions/loading-desc signal-id tr)})
      (when-not (<sub [::process/signal-forbidden? target-id signal])
        ;; do not show button at all if signal is forbidden (aka is not expected to become available for this user)
        (if confirm?
          [ui/modal-confirmation-button
           (p/assoc-if
             {:trigger-button-text (tr [signal-id])
              :trigger-button-opts button-opts
              :tr                  tr
              :id                  (str signal-id)
              :on-yes-click        signal-send-fn
              :yes-disabled?       (not params-valid?)}
             :modal-title         confirm-title)
           confirm-content]
          (cc/button
            (assoc button-opts :on-click signal-send-fn)
            (tr [signal-id])))))))

(defn signal-button
  "A button for the action bar that sends a tilakone signal to the target entity when clicked.
   Optionally pops up a confirmation dialog, with an extra-optional custom body

   Required props:
   `:tr`, `target-entity` (can be just id), `:signal`

   Optional props:
   `:button-opts`    (props that override the button's default options)
   `:confirm?`       (true if a pop up dialog should confirm sending the signal)
   `:confirm-title`  (a string shown on the confirmation dialog; default is \"Are you sure?\")
   `:confirm-params` (a map of the signal parameters that the dialog should take from the user;
                      it is the same as the signal's param map except the values are a map,
                      as detailed below)

   The `:confirm-params` value props:
   `:component`     (the UI input component to use for the param, required)
   `:opts`          (options for the param input element, default {})
   `:required?`     (true if the field is required, falsy otherwise)
   `:default-value` (the value the field should initially have, default nil)"
  [{:keys              [tr target-entity button-opts confirm? confirm-title confirm-params]
    [signal-id params] :signal}]
  (r/with-let [*params (r/atom (p/map-vals #(assoc % :value (:default-value %)) confirm-params))]
    (let [params-merged  (cond-> params
                           (not-empty confirm-params) (merge (p/map-vals :value @*params)))
          coerce-changed #(cond-> % ;; Because some components' :on-change returns a JS event...
                            (instance? js/Object %) futil/tval)
          params-valid?  (params-valid? params-merged)]
      [signal-button-impl
       (util/mmap {:target-id (:db/id target-entity target-entity)
                   :params    params-merged}
                  signal-id confirm? tr button-opts *params confirm-title
                  params-valid?)
       (futil/for-frag [[param {:keys [component opts]}] confirm-params]
         [component
          (merge opts {:on-change #(swap! *params assoc-in [param :value] (coerce-changed %))})])])))

(defn primary-signal-button
  "A button that attempts to mimic the top bar primary action button as much as possible,
  including parameters modal etc.

  Button content and opts are optional.

  `process-sub` is optional if target entity is a workspace phase"
  [{:keys [target-id signal-id tr button-opts process-sub]} & [content]]
  (let [process-target            (<sub [::entities/entity target-id])
        [{:keys [confirm? status]
          :as   action-def}]      (<sub [::action-subs/actions-for-signal
                                         (or process-sub [::process/entity-process target-id])
                                         signal-id])
        disabled?                 (= status :disabled)
        button-opts               (merge (button-default-opts signal-id)
                                         {:disabled disabled?}
                                         button-opts)]
    (when action-def
      (cc/button
        (assoc button-opts :on-click #(primary-actions/handle-action (util/mmap {:process-target process-target}
                                                                                signal-id confirm?)))
        (or content (tr [signal-id]))))))
